import { useEffect, useRef, useState } from 'react';

/**
 * IntersectionObserver-based scroll reveal hook.
 * Returns a ref to attach to a DOM element and an `isVisible` flag that
 * becomes true once the element enters the viewport.
 *
 * On desktop (element already in viewport on mount) the flag is set
 * synchronously so there is no flash-of-invisible content.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options?: Partial<IntersectionObserverInit>,
) {
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If the element is already in the viewport (desktop, top of page), reveal immediately.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight - 20) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px', ...options },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
}
